import { useEffect, useRef } from 'react'
import { Loader2, Users } from 'lucide-react'
import { SearchableSelect } from '@/components/ui/SearchableSelect'
import { SectionCard } from '@/components/ui/SectionCard'
import { useUserRole } from '@/hooks/useUserRole'
import { useYardiInviteAdd } from '../hooks/useYardiInviteAdd'
import { yardiInviteAddSchema } from '../utils/yardiInviteValidators'
import { PageHeader } from '@/features/invitee/components/PageHeader'

export function YardiInviteAddPage() {
  const { propertyId, companyId, propertyName, companyName } = useUserRole()
  const add = useYardiInviteAdd({
    propertyId: propertyId != null ? String(propertyId) : null,
    companyId: companyId != null ? String(companyId) : null,
    propertyName: propertyName != null ? String(propertyName) : null,
    companyName: companyName != null ? String(companyName) : null,
  })

  const parsed = yardiInviteAddSchema.safeParse({
    company_id: add.companyId,
    property_id: add.propertyId,
    prospect_id: add.prospectId,
  })
  const fieldErrors = add.validateApplicationForm && !parsed.success ? parsed.error.flatten().fieldErrors : {}

  /** Angular `@HostListener('focusout')` — native `focusout` bubbles; `blur` does not. */
  const formShellRef = useRef<HTMLDivElement>(null)
  // Stable (empty-dep useCallback) — destructure so the listener effect doesn't depend on the per-render `add` object.
  const { onFocusOutValidate } = add
  useEffect(() => {
    const el = formShellRef.current
    if (!el) return
    const fn = () => onFocusOutValidate()
    el.addEventListener('focusout', fn)
    return () => el.removeEventListener('focusout', fn)
  }, [onFocusOutValidate])

  /** Initial permission + company list only — submit/poll loading stays on the Send Invitation button. */
  const cardLoading = add.initialPageLoading

  return (
    <div
      className="min-h-screen bg-[#f8fafc] pb-12 font-sans text-slate-900"
      data-testid="yardi-invite-add-page"
      style={{ padding: 0 }}
    >
      <PageHeader title="ADD YARDI INVITE" testIdRoot="yardi-invite-add-page-header" />

      <div className="mx-auto w-full px-3 py-4 sm:px-6 sm:py-8 lg:px-8">
        <div
          className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm ring-1 ring-slate-100"
          data-testid="yardi-invite-add-main-card"
        >
          <div className="min-h-[280px] w-full min-w-0 bg-slate-50/50 p-4 sm:min-h-[300px] sm:p-6">
            {cardLoading ? (
              <div
                data-testid="yardi-invite-add-loading"
                className="flex min-h-[280px] flex-1 items-center justify-center py-20"
              >
                <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" aria-label="Loading" />
              </div>
            ) : (
              <div
                ref={formShellRef}
                aria-busy={add.inviteSubmitInFlight}
                data-testid="yardi-invite-add-form"
              >
                <SectionCard
                  icon={Users}
                  title="Occupant Information"
                  testId="yardi-invite-add-section-occupant"
                  titleTestId="yardi-invite-add-section-occupant-heading"
                >
                  <div className="grid gap-6 md:grid-cols-3">
                    <div className={add.readOnlyControl.includes('company') ? 'pointer-events-none opacity-70' : ''}>
                      <label
                        className="mb-1 block text-sm font-medium text-slate-700"
                        data-testid="yardi-invite-add-company-label"
                      >
                        Company Name *
                      </label>
                      <SearchableSelect
                        options={add.companyOptions}
                        value={add.companyId}
                        disabled={add.inviteSubmitInFlight}
                        onChange={(v) => {
                          const id = String(v)
                          if (id === add.companyId) return
                          add.setCompanyId(id)
                          add.companySelected(id)
                        }}
                        placeholder="All Company"
                        searchPlaceholder="Search By Company Name"
                        onSearch={add.searchCompany}
                        searchDebounceMs={300}
                        onOpen={() => add.loadCompanies('')}
                        invokeSearchOnClose={false}
                        error={fieldErrors.company_id?.[0]}
                        triggerDataTestId="yardi-invite-add-company"
                      />
                    </div>
                    <div className={add.readOnlyControl.includes('property') ? 'pointer-events-none opacity-70' : ''}>
                      <label
                        className="mb-1 block text-sm font-medium text-slate-700"
                        data-testid="yardi-invite-add-property-label"
                      >
                        Property Name *
                      </label>
                      <SearchableSelect
                        options={add.propertyOptions}
                        value={add.propertyId}
                        disabled={add.inviteSubmitInFlight}
                        onChange={(v) => add.setPropertyId(String(v))}
                        placeholder="All Properties"
                        searchPlaceholder="Search By Property Name"
                        onSearch={add.searchProperty}
                        searchDebounceMs={300}
                        onOpen={() => add.loadProperties('', add.companyId)}
                        invokeSearchOnClose={false}
                        error={fieldErrors.property_id?.[0]}
                        triggerDataTestId="yardi-invite-add-property"
                      />
                    </div>
                    <div data-testid="yardi-invite-add-prospect-field">
                      <label
                        className="mb-1 block text-sm font-medium text-slate-700"
                        htmlFor="prospect_id"
                        data-testid="yardi-invite-add-prospect-label"
                      >
                        Prospect ID *
                      </label>
                      <input
                        id="prospect_id"
                        data-testid="yardi-invite-add-prospect-id-input"
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-600 disabled:opacity-70"
                        value={add.prospectId}
                        disabled={add.inviteSubmitInFlight}
                        onChange={(e) => add.setProspectId(e.target.value)}
                      />
                      {fieldErrors.prospect_id?.[0] && (
                        <p
                          className="mt-1 text-xs text-red-600"
                          data-testid="yardi-invite-add-prospect-error"
                        >
                          {fieldErrors.prospect_id[0]}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      disabled={add.inviteBlocking}
                      data-testid="yardi-invite-add-send-button"
                      className="inline-flex min-w-[10.5rem] items-center justify-center gap-2 rounded-xl bg-[rgb(21_151_255)] px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-[rgba(21,151,255,0.25)] transition-all hover:-translate-y-0.5 hover:bg-[#0d7acc] hover:shadow-xl disabled:pointer-events-none disabled:opacity-50 disabled:hover:translate-y-0"
                      onClick={() => void add.submitForm()}
                    >
                      {add.inviteSubmitInFlight ? (
                        <>
                          <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                          <span>Processing…</span>
                        </>
                      ) : (
                        'Send Invitation'
                      )}
                    </button>
                    <button
                      type="button"
                      disabled={add.inviteBlocking}
                      data-testid="yardi-invite-add-reset-button"
                      className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:shadow-md disabled:pointer-events-none disabled:opacity-50"
                      onClick={() => add.resetForm()}
                    >
                      Reset
                    </button>
                  </div>
                </SectionCard>

                {add.result.status !== '' && (
                  <div
                    className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4"
                    data-testid="yardi-invite-add-result-section"
                  >
                    <h3 className="text-sm font-semibold text-slate-800" data-testid="yardi-invite-add-result-heading">
                      Result
                    </h3>
                    <table className="mt-2 w-full text-sm" data-testid="yardi-invite-add-result-table">
                      <thead data-testid="yardi-invite-add-result-table-head">
                        <tr>
                          <th
                            className="text-left font-medium text-slate-600"
                            data-testid="yardi-invite-add-result-col-status"
                          >
                            Status
                          </th>
                          <th
                            className="text-left font-medium text-slate-600"
                            data-testid="yardi-invite-add-result-col-reason"
                          >
                            Reason
                          </th>
                        </tr>
                      </thead>
                      <tbody data-testid="yardi-invite-add-result-table-body">
                        <tr data-testid="yardi-invite-add-result-row">
                          <td
                            className="w-1/4 py-1 align-top"
                            data-testid="yardi-invite-add-result-status-cell"
                          >
                            {add.inviteSubmitInFlight ? (
                              <span className="inline-flex items-center gap-2 text-slate-700">
                                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[rgb(21_151_255)]" aria-hidden />
                                Processing…
                              </span>
                            ) : (
                              add.result.status
                            )}
                          </td>
                          <td
                            className="py-1 align-top text-slate-700"
                            data-testid="yardi-invite-add-result-reason-cell"
                          >
                            {add.inviteSubmitInFlight ? '—' : add.result.reason}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
